/**
 * RendPleX Scenario Analyzer 2.0 — P3B Financing Strategy Comparison.
 *
 * Renders five deterministic strategies (Current, More Equity,
 * Better Rate, Lower Price, Optimized) from a single
 * POST /api/scenario/financing-strategies request. Every metric
 * comes from the canonical engine on the backend — this component
 * performs zero financial math beyond reading the payload.
 *
 * Explicit "Compare Financing Strategies" action; no per-slider
 * network calls.
 */
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import { toast } from 'sonner';
import { Button } from '../../ui/button';
import {
  Layers, Loader2, CheckCircle2, AlertTriangle, MinusCircle,
  ArrowUp, ArrowDown, Minus,
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const STRATEGY_META = {
  current:      { i18n: 'financing.strategyCurrent',     tone: 'slate'  },
  more_equity:  { i18n: 'financing.strategyMoreEquity',  tone: 'indigo' },
  better_rate:  { i18n: 'financing.strategyBetterRate',  tone: 'indigo' },
  lower_price:  { i18n: 'financing.strategyLowerPrice',  tone: 'indigo' },
  optimized:    { i18n: 'financing.strategyOptimized',   tone: 'emerald' },
};

const REASON_I18N = {
  down_payment_at_max:      'financing.reasonDpAtMax',
  interest_rate_at_min:     'financing.reasonRateAtMin',
  offer_target_unavailable: 'financing.reasonOfferUnavailable',
  optimization_not_feasible:'financing.reasonOptNotFeasible',
  already_met:              'financing.reasonAlreadyMet',
};

const fmtMoney = (n) => (n === null || n === undefined || Number.isNaN(n))
  ? '—'
  : `$${Math.round(n).toLocaleString()}`;
const fmtPct = (n, dec = 2) => (n === null || n === undefined || Number.isNaN(n))
  ? '—'
  : `${n.toFixed(dec)}%`;
const fmtRatio = (n, dec = 2) => (n === null || n === undefined || Number.isNaN(n))
  ? '—'
  : n.toFixed(dec);
const fmtSignedMoney = (n) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const sign = n > 0 ? '+' : (n < 0 ? '−' : '');
  return `${sign}$${Math.abs(Math.round(n)).toLocaleString()}`;
};
const fmtSignedRatio = (n, dec = 2) => {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  const sign = n > 0 ? '+' : (n < 0 ? '−' : '');
  return `${sign}${Math.abs(n).toFixed(dec)}`;
};

const DeltaBadge = ({ delta, kind = 'money', dec = 2 }) => {
  if (delta === null || delta === undefined || Number.isNaN(delta)) {
    return <span className="text-slate-400">—</span>;
  }
  const zero = Math.abs(delta) < (kind === 'money' ? 0.5 : 0.005);
  const positive = delta > 0 && !zero;
  // Note: "positive delta" is not always "good" — for cash_required
  // more cash is worse. Callers pass `goodWhenNegative` via kind.
  const cls = zero
    ? 'text-slate-500 dark:text-slate-400'
    : positive
      ? 'text-emerald-600 dark:text-emerald-400'
      : 'text-red-600 dark:text-red-400';
  const Icon = zero ? Minus : (positive ? ArrowUp : ArrowDown);
  const formatted = kind === 'money' ? fmtSignedMoney(delta) : fmtSignedRatio(delta, dec);
  return (
    <span className={`inline-flex items-center gap-0.5 tabular-nums ${cls}`}>
      <Icon className="w-3 h-3" />{formatted}
    </span>
  );
};

// For "cash_required" a positive delta means MORE cash needed → tone
// should invert. Custom badge:
const CashDeltaBadge = ({ delta }) => {
  if (delta === null || delta === undefined || Number.isNaN(delta)) {
    return <span className="text-slate-400">—</span>;
  }
  const zero = Math.abs(delta) < 0.5;
  const positive = delta > 0 && !zero;
  const cls = zero
    ? 'text-slate-500 dark:text-slate-400'
    : positive
      ? 'text-amber-600 dark:text-amber-400'   // more cash is a friction
      : 'text-emerald-600 dark:text-emerald-400';
  const Icon = zero ? Minus : (positive ? ArrowUp : ArrowDown);
  return (
    <span className={`inline-flex items-center gap-0.5 tabular-nums ${cls}`}>
      <Icon className="w-3 h-3" />{fmtSignedMoney(delta)}
    </span>
  );
};

const StrategyCard = ({ strategy, onApply }) => {
  const { t } = useTranslation();
  const meta = STRATEGY_META[strategy.strategy_id] || STRATEGY_META.current;
  const isAvailable = strategy.status === 'available';
  const isNa = strategy.status === 'not_applicable';
  const isUnavail = strategy.status === 'unavailable';
  const isCurrent = strategy.strategy_id === 'current';

  return (
    <div
      data-testid={`fs-card-${strategy.strategy_id}`}
      data-status={strategy.status}
      className={`rounded-xl border p-4 flex flex-col gap-3 ${
        strategy.strategy_id === 'current'
          ? 'border-slate-300 dark:border-slate-700 bg-slate-50 dark:bg-[#0F1419]'
          : strategy.strategy_id === 'optimized' && isAvailable
            ? 'border-emerald-300 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-950/20'
            : 'border-indigo-200 dark:border-indigo-900 bg-white dark:bg-[#0F1419]'
      }`}
    >
      <div className="flex items-start justify-between gap-2">
        <div>
          <h3 className="font-primary font-bold text-sm text-slate-900 dark:text-slate-100 uppercase tracking-wider">
            {t(meta.i18n)}
          </h3>
          {isNa && (
            <p data-testid={`fs-reason-${strategy.strategy_id}`} className="text-[11px] text-slate-500 dark:text-slate-400 mt-0.5 flex items-center gap-1">
              <MinusCircle className="w-3 h-3" />
              {t(REASON_I18N[strategy.reason_code] || 'financing.reasonGeneric')}
            </p>
          )}
          {isUnavail && (
            <p data-testid={`fs-reason-${strategy.strategy_id}`} className="text-[11px] text-amber-600 dark:text-amber-400 mt-0.5 flex items-center gap-1">
              <AlertTriangle className="w-3 h-3" />
              {t(REASON_I18N[strategy.reason_code] || 'financing.reasonGeneric')}
            </p>
          )}
          {strategy.strategy_id === 'optimized' && isAvailable && strategy.reason_code === 'already_met' && (
            <p data-testid="fs-optimized-already-met" className="text-[11px] text-emerald-600 dark:text-emerald-400 mt-0.5 flex items-center gap-1">
              <CheckCircle2 className="w-3 h-3" />
              {t('financing.reasonAlreadyMet')}
            </p>
          )}
          {strategy.strategy_id === 'better_rate' && isAvailable && (
            <p className="text-[10px] text-slate-500 dark:text-slate-400 italic mt-0.5">
              {t('financing.betterRateGuardrail')}
            </p>
          )}
        </div>
        {isAvailable && !isCurrent && (
          <Button
            data-testid={`fs-apply-${strategy.strategy_id}`}
            size="sm"
            variant="outline"
            className="text-xs h-8"
            onClick={() => onApply(strategy.scenario)}
          >
            {t('financing.applyStrategy')}
          </Button>
        )}
      </div>

      {isAvailable ? (
        <div className="grid grid-cols-2 gap-y-1.5 gap-x-3 text-xs">
          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricPrice')}</div>
          <div data-testid={`fs-price-${strategy.strategy_id}`} className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtMoney(strategy.purchase_price)}
          </div>

          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricDpPct')}</div>
          <div data-testid={`fs-dp-pct-${strategy.strategy_id}`} className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtPct(strategy.down_payment_percent, 1)}
          </div>

          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricDpDollars')}</div>
          <div data-testid={`fs-dp-dollars-${strategy.strategy_id}`} className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtMoney(strategy.down_payment_dollars)}
          </div>

          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricMortgageAmount')}</div>
          <div className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtMoney(strategy.mortgage_amount)}
          </div>

          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricRate')}</div>
          <div data-testid={`fs-rate-${strategy.strategy_id}`} className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtPct(strategy.interest_rate, 2)}
          </div>

          <div className="pt-1 border-t border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400">{t('financing.metricMortgagePmt')}</div>
          <div className="pt-1 border-t border-slate-200 dark:border-slate-800 text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtMoney(strategy.metrics?.monthly_mortgage)}
          </div>

          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricMcf')}</div>
          <div data-testid={`fs-mcf-${strategy.strategy_id}`} className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtMoney(strategy.metrics?.monthly_cash_flow)}
            {!isCurrent && strategy.deltas?.monthly_cash_flow !== null && (
              <span className="ml-1 text-[10px]"><DeltaBadge delta={strategy.deltas.monthly_cash_flow} /></span>
            )}
          </div>

          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricNoi')}</div>
          <div className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtMoney(strategy.metrics?.noi)}
          </div>

          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricCap')}</div>
          <div className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtPct(strategy.metrics?.cap_rate)}
          </div>

          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricDscr')}</div>
          <div data-testid={`fs-dscr-${strategy.strategy_id}`} className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtRatio(strategy.metrics?.dscr)}
            {!isCurrent && strategy.deltas?.dscr !== null && (
              <span className="ml-1 text-[10px]"><DeltaBadge delta={strategy.deltas.dscr} kind="ratio" /></span>
            )}
          </div>

          <div className="text-slate-500 dark:text-slate-400">{t('financing.metricCoc')}</div>
          <div className="text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtPct(strategy.metrics?.cash_on_cash)}
            {!isCurrent && strategy.deltas?.cash_on_cash !== null && (
              <span className="ml-1 text-[10px]"><DeltaBadge delta={strategy.deltas.cash_on_cash} kind="ratio" /></span>
            )}
          </div>

          <div className="pt-1 border-t border-slate-200 dark:border-slate-800 text-slate-500 dark:text-slate-400">{t('financing.metricCashRequired')}</div>
          <div className="pt-1 border-t border-slate-200 dark:border-slate-800 text-right font-mono tabular-nums text-slate-900 dark:text-slate-100">
            {fmtMoney(strategy.cash_required)}
            {!isCurrent && (
              <span data-testid={`fs-cash-delta-${strategy.strategy_id}`} className="ml-1 text-[10px]"><CashDeltaBadge delta={strategy.deltas?.cash_required} /></span>
            )}
          </div>
        </div>
      ) : (
        <p className="text-xs text-slate-400 italic">{t('financing.noMetrics')}</p>
      )}
    </div>
  );
};

const FinancingStrategiesTab = ({ scenario, onApply }) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [targetValue, setTargetValue] = useState(500);

  const run = async () => {
    setLoading(true);
    setError(null);
    setData(null);
    try {
      const resp = await axios.post(
        `${BACKEND_URL}/api/scenario/financing-strategies`,
        {
          scenario,
          target_metric: 'monthly_cash_flow',
          target_value: Number(targetValue),
          locked: [],
        },
        { withCredentials: true }
      );
      setData(resp.data);
    } catch (e) {
      setError(e.response?.data?.detail || t('financing.errorGeneric'));
    } finally {
      setLoading(false);
    }
  };

  const applyStrategy = (strategyScenario) => {
    if (!strategyScenario) return;
    onApply({
      purchase_price: strategyScenario.purchase_price,
      annual_income: strategyScenario.annual_income,
      down_payment_percent: strategyScenario.down_payment_percent,
      interest_rate: strategyScenario.interest_rate,
    });
    toast.success(t('financing.applied'));
  };

  return (
    <div
      data-testid="financing-strategies-tab"
      role="region"
      aria-live="polite"
      aria-atomic="false"
      className="space-y-4"
    >
      {/* Controls */}
      <div className="grid grid-cols-1 md:grid-cols-[minmax(0,1fr)_auto] gap-3 items-end">
        <div>
          <label className="text-xs uppercase tracking-wider text-slate-500 dark:text-slate-400 font-semibold mb-1 block">
            {t('financing.targetForOptimizations')}
          </label>
          <div className="relative max-w-xs">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">$</span>
            <input
              data-testid="financing-target-value"
              type="number"
              step={50}
              value={targetValue}
              onChange={(e) => {
                const raw = e.target.value;
                const n = raw === '' ? 0 : Number(raw);
                if (Number.isFinite(n)) setTargetValue(n);
              }}
              className="w-full h-10 rounded-lg border border-slate-300 dark:border-slate-700 bg-white dark:bg-[#0F1419] text-slate-900 dark:text-slate-100 pl-7 pr-14 text-sm font-semibold focus:outline-none focus:ring-2 focus:ring-indigo-500"
            />
            <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs">/mo</span>
          </div>
          <p className="text-[10px] text-slate-500 dark:text-slate-400 mt-1">
            {t('financing.targetHelp')}
          </p>
        </div>
        <Button
          data-testid="financing-run-btn"
          onClick={run}
          disabled={loading}
          className="h-10 bg-indigo-600 hover:bg-indigo-700 text-white"
        >
          {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" />{t('financing.computing')}</>)
                   : (<><Layers className="w-4 h-4 mr-2" />{t('financing.compare')}</>)}
        </Button>
      </div>

      {error && (
        <p data-testid="financing-error" className="text-sm text-red-600 dark:text-red-400">{error}</p>
      )}

      {data && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-3">
            {data.strategies.map((s) => (
              <StrategyCard key={s.strategy_id} strategy={s} onApply={applyStrategy} />
            ))}
          </div>
          <p className="text-[11px] text-slate-500 dark:text-slate-400 italic">
            {t('financing.deterministicNote')}
          </p>
        </>
      )}
    </div>
  );
};

export default FinancingStrategiesTab;
